import React from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import {
  ErrorOutlineOutlined as ErrorOutline,
  Refresh,
  Home,
  SystemUpdateAlt,
} from '@mui/icons-material';
import {
  isChunkLoadError,
  isReloadingForChunkError,
  reloadOnceForChunkError,
} from '../utils/chunkReload';

/**
 * Error Boundary Component
 * Vangt JavaScript errors in child componenten op en toont een fallback UI
 * Dit voorkomt dat de hele app crasht bij een error in één component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state zodat de volgende render de fallback UI toont
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Chunk ontbreekt na een deploy: één keer automatisch herladen (zie utils/chunkReload).
    // Lukt dat niet (net al geprobeerd), dan toont render() de melding "nieuwe versie".
    if (isChunkLoadError(error)) {
      if (reloadOnceForChunkError()) return;
      console.warn('Pagina-onderdeel kon niet geladen worden, ook niet na herladen:', error);
      return;
    }

    // Log de error naar een error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // Optioneel: stuur error naar externe logging service
    // Dit kan later gekoppeld worden aan Sentry, LogRocket, etc.
    if (import.meta.env.PROD) {
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService = (error, errorInfo) => {
    // Placeholder voor error logging service
    // In productie kan dit naar Sentry, LogRocket, etc. gestuurd worden
    try {
      const errorData = {
        message: error?.message || 'Unknown error',
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // Log naar console in development, naar service in production
      console.error('Error logged:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  renderNewVersion() {
    const reloading = isReloadingForChunkError();
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <SystemUpdateAlt sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Er is een nieuwe versie van BEM.report beschikbaar
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            {reloading
              ? 'De nieuwe versie wordt geladen…'
              : 'Herlaad de pagina om verder te werken met de nieuwste versie.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={this.handleReload}
            disabled={reloading}
            sx={{ borderRadius: 2 }}
          >
            Pagina herladen
          </Button>
        </Paper>
      </Container>
    );
  }

  render() {
    if (this.state.hasError) {
      // Ontbrekende chunk na een deploy (of de vervolgfout terwijl we al herladen):
      // geen crash-scherm maar een nette melding
      if (isChunkLoadError(this.state.error) || isReloadingForChunkError()) {
        return this.renderNewVersion();
      }

      // Custom fallback UI
      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'error.light',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <ErrorOutline
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                }}
              >
                Er is iets misgegaan
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                }}
              >
                Er is een onverwachte fout opgetreden. Onze excuses voor het ongemak.
              </Typography>
            </Box>

            {/* Foutdetails en stacktrace alleen in development; in productie staat de fout in de console */}
            {import.meta.env.DEV && this.state.error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleRetry}
                sx={{ borderRadius: 2 }}
              >
                Probeer opnieuw
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{ borderRadius: 2 }}
              >
                Pagina herladen
              </Button>
              <Button
                variant="text"
                startIcon={<Home />}
                onClick={this.handleGoHome}
                sx={{ borderRadius: 2 }}
              >
                Naar home
              </Button>
            </Box>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
